import React, { useState, useRef, useEffect } from 'react';
import { format, parseISO } from 'date-fns';
import { DateRange } from 'react-date-range';
import { enUS } from 'date-fns/locale';
import styled from 'styled-components';
import { FileDown, Share2, Check, Settings } from 'lucide-react';
import DomainSelect from './DomainSelect';
import LandingSelect from './LandingSelect';
import CountrySelect from './CountrySelect';
import TrafficSourceSelect from './TrafficSourceSelect';
import ColumnFilters from './ColumnFilters';

const DateRangePickerWrapper = styled.div`
  display: inline-block;
  position: relative;
`;

const DateRangeInput = styled.input`
  padding: 8px 12px;
  font-size: 16px;
  height: 37px;
  color: #555;
  background-color: #fff;
  background-image: none;
  border: 1px solid #ccc;
  border-radius: 4px;
  transition: border-color ease-in-out 0.15s,box-shadow ease-in-out 0.15s;
  cursor: pointer;
`;

const DateRangePickerPopup = styled.div`
  background-color: #fff;
  position: absolute;
  top: 100%;
  left: 0;
  z-index: 9999;
  box-shadow: 0 4px 8px rgba(0,0,0,0.1);
`;

const ButtonsContainer = styled.div`
  display: flex;
  gap: 8px;
`;

const SubmitButton = styled.button`
  padding: 8px 20px;
  font-size: 14px;
  font-weight: bold;
  color: #fff;
  background-color: #007bff;
  border: none;
  border-radius: 4px;
  cursor: pointer;
  transition: background-color 0.2s;

  &:hover {
    background-color: #0056b3;
  }

  &:disabled {
    background-color: #ccc;
    cursor: not-allowed;
  }
`;

const ActionButton = styled.button`
  padding: 8px 16px;
  font-size: 14px;
  font-weight: medium;
  color: #fff;
  border: none;
  border-radius: 4px;
  cursor: pointer;
  transition: all 0.2s;
  display: flex;
  align-items: center;
  gap: 6px;

  &:hover {
    opacity: 0.9;
  }

  &:disabled {
    opacity: 0.7;
    cursor: not-allowed;
  }
`;

const ExportButton = styled(ActionButton)`
  background-color: #10b981;
  
  &:hover {
    background-color: #059669;
  }
`;

const ShareButton = styled(ActionButton)`
  background-color: #3b82f6;
  
  &:hover {
    background-color: #2563eb;
  }
`;

const Preloader = styled.div`
  display: inline-block;
  margin-left: 10px;
  vertical-align: middle;
  width: 20px;
  height: 20px;
  border-radius: 50%;
  border: 2px solid #ccc;
  border-top-color: #007bff;
  animation: spin 1s infinite linear;

  @keyframes spin {
    to {
      transform: rotate(360deg);
    }
  }
`;

const customLocale = {
  ...enUS,
  options: {
    ...enUS.options,
    weekStartsOn: 1
  }
};

// Вспомогательная функция для проверки и преобразования дат
const ensureDate = (date) => {
  if (!date) {
    return null;
  }
  
  if (date instanceof Date) {
    return date;
  }
  
  if (typeof date === 'string') {
    try {
      return parseISO(date);
    } catch (e) {
      console.error('Error parsing date string:', date, e);
      return null;
    }
  }
  
  return null;
};

// Вспомогательная функция для форматирования даты с проверкой
const formatSafeDate = (date, dateFormat) => {
  try {
    const safeDate = ensureDate(date);
    if (!safeDate) {
      return 'Invalid date';
    }
    return format(safeDate, dateFormat);
  } catch (e) {
    console.error('Error formatting date:', date, e);
    return 'Invalid date';
  }
};

// Компонент для отображения тоста при копировании ссылки
const CopyNotification = ({ show }) => {
  if (!show) return null;
  
  return (
    <div className="fixed bottom-4 right-4 bg-gray-800 text-white px-4 py-2 rounded-md shadow-lg z-50 flex items-center gap-2 transform transition-all duration-300 ease-out translate-y-0 opacity-100">
      <Check className="h-4 w-4 text-green-400" />
      Link copied to clipboard!
    </div>
  );
};

const StatisticsFilters = ({
  domains,
  landings,
  setDomains,
  setLandings,
  selectedDomain,
  selectedLandings,
  selectedCountry,
  selectedDateRange,
  selectedTrafficSource,
  setSelectedDomain,
  setSelectedLandings,
  setSelectedCountry,
  setSelectedDateRange,
  setSelectedTrafficSource,
  landingOptions,
  setLandingOptions,
  visibleColumns,
  setVisibleColumns,
  columnOrder,
  isLoading,
  handleGetStatistics,
  columnsMetadata, // ← Изменено с columns на columnsMetadata
  isMetadataLoading, // ← Добавлено состояние загрузки метаданных
  updateQueryParams,
  handleCopyLink,
  downloadCSV,
  copySuccess
}) => {
  const [isDateRangePickerOpen, setIsDateRangePickerOpen] = useState(false);
  const dateRangePickerRef = useRef(null);
  
  useEffect(() => {
    const handleOutsideClick = (event) => {
      if (dateRangePickerRef.current && !dateRangePickerRef.current.contains(event.target)) {
        setIsDateRangePickerOpen(false);
      }
    };
    
    document.addEventListener('mousedown', handleOutsideClick);
    return () => {
      document.removeEventListener('mousedown', handleOutsideClick);
    };
  }, []);
  
  const handleDomainChange = (event) => {
    const selectedDomainId = event.target.value;
    setSelectedDomain(selectedDomainId);
    setSelectedLandings(['all']);
    updateQueryParams({
      domain: selectedDomainId,
      landings: ['all'],
      visibleColumns,
      columnOrder,
      country: selectedCountry,
      trafficSource: selectedTrafficSource,
      dateRange: selectedDateRange
    });
  };

  const handleLandingChange = (newLandings) => {
    setSelectedLandings(newLandings);
    updateQueryParams({
      domain: selectedDomain,
      landings: newLandings,
      visibleColumns,
      columnOrder,
      country: selectedCountry,
      trafficSource: selectedTrafficSource,
      dateRange: selectedDateRange
    });
  };

  const handleCountryChange = (newCountry) => {
    setSelectedCountry(newCountry);
    updateQueryParams({
      domain: selectedDomain,
      landings: selectedLandings,
      country: newCountry,
      trafficSource: selectedTrafficSource,
      visibleColumns,
      columnOrder,
      dateRange: selectedDateRange
    });
  };

  const handleTrafficSourceChange = (newTrafficSource) => {
    setSelectedTrafficSource(newTrafficSource);
    updateQueryParams({
      domain: selectedDomain,
      landings: selectedLandings,
      country: selectedCountry,
      trafficSource: newTrafficSource,
      visibleColumns,
      columnOrder,
      dateRange: selectedDateRange
    });
  };
  
  const handleDateRangeFieldClick = () => {
    setIsDateRangePickerOpen(!isDateRangePickerOpen);
  };
  
  const handleDateRangeChange = (ranges) => {
    setSelectedDateRange([ranges.selection]);
    updateQueryParams({
      domain: selectedDomain,
      landings: selectedLandings,
      country: selectedCountry,
      trafficSource: selectedTrafficSource,
      dateRange: [ranges.selection],
      visibleColumns,
      columnOrder
    });
  };

  // Добавляем информацию о выбранных фильтрах в тултип для кнопок
  const getFiltersSummary = () => {
    const domainText = selectedDomain === 'all' 
      ? 'All Domains' 
      : domains.find(d => d.id === parseInt(selectedDomain))?.name || 'All Domains';
    
    const landingText = selectedLandings.includes('all') 
      ? 'All Landings' 
      : selectedLandings.map(id => 
          landings.find(l => l.id.toString() === id)?.name
        ).filter(Boolean).join(', ') || 'All Landings';
    
    const countryText = selectedCountry?.label || 'All Countries';
    const trafficSourceText = selectedTrafficSource?.label || 'All Sources';
    
    let dateText = 'Invalid date range';
    try {
      if (selectedDateRange && selectedDateRange[0]) {
        const startDate = ensureDate(selectedDateRange[0].startDate);
        const endDate = ensureDate(selectedDateRange[0].endDate);
        
        if (startDate && endDate) {
          dateText = `${formatSafeDate(startDate, 'dd/MM/yyyy')} - ${formatSafeDate(endDate, 'dd/MM/yyyy')}`;
        }
      }
    } catch (e) {
      console.error('Error generating date text:', e);
    }
    
    return `Domain: ${domainText} | Landing: ${landingText} | Country: ${countryText} | Source: ${trafficSourceText} | Period: ${dateText}`;
  };

  // Форматированное представление диапазона дат в поле ввода
  const getFormattedDateRange = () => {
    try {
      if (!selectedDateRange || !selectedDateRange[0]) {
        return 'Select date range';
      }
      
      const startDate = ensureDate(selectedDateRange[0].startDate);
      const endDate = ensureDate(selectedDateRange[0].endDate);
      
      if (!startDate || !endDate) {
        return 'Invalid date range';
      }
      
      return `${formatSafeDate(startDate, 'dd/MM/yyyy')} - ${formatSafeDate(endDate, 'dd/MM/yyyy')}`;
    } catch (e) {
      console.error('Error formatting date range:', e);
      return 'Invalid date range';
    }
  };

  // Проверяем, что диапазон дат содержит объекты Date (для react-date-range)
  const getNormalizedDateRange = () => {
    if (!selectedDateRange || !selectedDateRange[0]) {
      // Возвращаем текущий день, если нет диапазона
      const today = new Date();
      return [{
        startDate: today,
        endDate: today,
        key: 'selection'
      }];
    }
    
    return selectedDateRange.map(range => ({
      ...range,
      startDate: ensureDate(range.startDate) || new Date(),
      endDate: ensureDate(range.endDate) || new Date(),
      key: range.key || 'selection'
    }));
  };

  return (
    <div className="block sticky-top bg-white p-4 rounded-lg shadow-sm mb-6">
      <div className="flex flex-wrap justify-between items-end gap-4">
        <div className="flex flex-wrap gap-4 flex-grow">
          {/* Все существующие фильтры остаются */}
          <DomainSelect
            domains={domains}
            selectedDomain={selectedDomain}
            onChange={handleDomainChange}
            setDomains={setDomains}
          />

          <LandingSelect
            landings={landingOptions}
            selectedLandings={selectedLandings}
            onChange={handleLandingChange}
            setLandings={setLandings}
          />

          <CountrySelect
            selectedCountry={selectedCountry}
            onChange={handleCountryChange}
          />
          
          <TrafficSourceSelect
            selectedTrafficSource={selectedTrafficSource}
            onChange={handleTrafficSourceChange}
          />
          
          {/* DateRangePicker остается как есть */}
          <DateRangePickerWrapper ref={dateRangePickerRef}>
            <DateRangeInput
              type="text"
              value={getFormattedDateRange()}
              readOnly
              onClick={handleDateRangeFieldClick}
              className="datepicker-input"
            />
            {isDateRangePickerOpen && (
              <DateRangePickerPopup>
                <DateRange
                  ranges={getNormalizedDateRange()}
                  onChange={handleDateRangeChange}
                  locale={customLocale}
                  months={1}
                  direction="horizontal"
                  showSelectionPreview={true}
                  moveRangeOnFirstSelection={false}
                />
              </DateRangePickerPopup>
            )}
          </DateRangePickerWrapper>
        </div>
        
        <ButtonsContainer className="flex flex-wrap items-center gap-2">
          <SubmitButton 
            onClick={handleGetStatistics}
            disabled={isLoading}
            title="Fetch statistics with current filters"
          >
            {isLoading ? <Preloader /> : 'Get Statistics'}
          </SubmitButton>
          
          <ExportButton 
            onClick={downloadCSV}
            title={`Export data as CSV (${getFiltersSummary()})`}
          >
            <FileDown size={16} />
            <span>Export CSV</span>
          </ExportButton>
          
          <ShareButton 
            onClick={handleCopyLink}
            disabled={copySuccess}
            title={`Copy sharable link (${getFiltersSummary()})`}
          >
            {copySuccess ? <Check size={16} className="animate-pulse" /> : <Share2 size={16} />}
            <span>{copySuccess ? 'Copied!' : 'Share Link'}</span>
          </ShareButton>
          
          {/* НОВАЯ КНОПКА - Ссылка на конструктор колонок */}
          <ActionButton
            as="a"
            href="/statistic_columns"
            target="_blank"
            rel="noopener noreferrer"
            title="Configure columns (opens in new tab)"
            style={{ backgroundColor: '#8B5CF6', textDecoration: 'none', color: 'white' }}
          >
            <Settings size={16} />
            <span>Configure Columns</span>
          </ActionButton>
          
          <div className="ml-2">
            <ColumnFilters 
              visibleColumns={visibleColumns}
              onColumnChange={setVisibleColumns}
              columnOrder={columnOrder}
              columnsMetadata={columnsMetadata} // ← Изменено с columns на columnsMetadata
              isMetadataLoading={isMetadataLoading} // ← Добавлено состояние загрузки
            />
          </div>
        </ButtonsContainer>
      </div>
      
      {/* Уведомление о копировании ссылки */}
      <CopyNotification show={copySuccess} />
    </div>
  );
};

export default StatisticsFilters;